import React, { useRef } from 'react';

import { Button, Group, Text, Title, createStyles } from '@mantine/core';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { IconCircleX, IconDownload, IconPhotoUp } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PRIMARY_SHADE_WHITE } from 'shared/constants/theme';

const useStyles = createStyles(() => ({
  dropzone: {
    borderWidth: 1,
    paddingBottom: 50,
    marginBottom: 20
  },
  button: {
    position: 'absolute',
    width: 260,
    left: 'calc(50% - 125px)',
    bottom: 20
  }
}));

interface ImageDropzoneProps {
  maxFileSizeBytes: number;
  onFileDrop: (file: FileWithPath) => void;
}

export function ImageDropzone({ onFileDrop, maxFileSizeBytes }: ImageDropzoneProps) {
  const { classes, theme } = useStyles();
  const openRef = useRef<() => void>(null);
  const { t } = useTranslation();

  return (
    <>
      <Dropzone
        openRef={openRef}
        onDrop={(files: FileWithPath[]) => onFileDrop(files[0])}
        className={classes.dropzone}
        accept={[MIME_TYPES.png, MIME_TYPES.jpeg]}
        multiple={false}
        maxSize={maxFileSizeBytes}>
        <Group position="center">
          <Dropzone.Accept>
            <IconDownload style={{ width: 50, height: 50 }} />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconCircleX style={{ width: 50, height: 50 }} color={theme.colors.red[PRIMARY_SHADE_WHITE]} />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconPhotoUp style={{ width: 50, height: 50 }} />
          </Dropzone.Idle>
        </Group>

        <Title order={3} align="center" mt="xl">
          <Dropzone.Accept>{t('IMAGE_DROPZONE.ACCEPT')}</Dropzone.Accept>
          <Dropzone.Reject>{t('IMAGE_DROPZONE.REJECT')}</Dropzone.Reject>
          <Dropzone.Idle>{t('IMAGE_DROPZONE.IDLE.UPLOAD')}</Dropzone.Idle>
        </Title>
        <Text align="center" mt="xs" color="dimmed">
          {t('IMAGE_DROPZONE.MESSAGE', { maxImageByteSize: maxFileSizeBytes / 1_000_000 })}
        </Text>
      </Dropzone>

      <Button className={classes.button} onClick={() => openRef.current?.()}>
        {t('IMAGE_DROPZONE.SELECT_FILE')}
      </Button>
    </>
  );
}
